import { ArrowUp } from 'lucide-react';
import { useTranslation } from '../../../../lib/i18n';
import { useItemSeenBi } from '../../../../lib/use-item-seen-bi';
import { CHIP_STYLE, type OpportunityCardData } from '../../../../lib/opportunity-card';
import { Card, Typography } from '../../ui';
import styles from './OpportunityCard.module.css';

/**
 * The opportunity widget, rendered UNDER an assistant reply that carries
 * `metadata.opportunityCard` — a sibling of `ConnectorConnectCard` and `CreditsCard`, riding its
 * reply the same way.
 *
 * Three parts, and it leads with the value rather than the machinery: the chip says which KIND of
 * gain this is, the title is the win itself, and the "how" line names the mechanism. What it
 * deliberately does NOT show is progress or status — that framing is what left goals invisible
 * behind a tab, telling the owner how far along something was instead of what they get.
 *
 * **It renders no buttons.** Its two CTAs are the reply's ordinary choice chips, minted from the
 * same tool call that recorded the card, so they appear just below via the normal chips path. A card
 * that reached a client without them is a server-side bug, not something to paper over here.
 */
export function OpportunityCard({ card }: { card: OpportunityCardData }) {
  const { t } = useTranslation();
  const seenRef = useItemSeenBi('opportunity_card', { additionalFields: { slug: card.slug, family: card.value } });
  const chip = card.value ? CHIP_STYLE[card.value] : undefined;

  return (
    <Card
      component="section"
      ref={seenRef}
      className={styles.card}
      aria-label={card.title}
      data-testid="chat-opportunity-card"
      data-family={card.value ?? 'none'}
    >
      {card.value && chip && (
        <p className={styles.chip} style={{ '--chip-hue': chip.hue } as React.CSSProperties}>
          <span className={styles.dot} aria-hidden="true" />
          {t(`chat.opportunityCard.chip.${card.value}`)}
          {/* An icon, not a typographic "↑": the native pair is held to that by its Golden Rule 3, and
            the three surfaces render the same glyph. */}
          {chip.rises && <ArrowUp className={styles.rise} size={12} strokeWidth={2.5} aria-hidden="true" />}
        </p>
      )}
      <Typography variant="h3" className={styles.win}>
        {card.title}
      </Typography>
      {card.how && (
        <p className={styles.how}>
          <span className={styles.howLabel}>{t('chat.opportunityCard.how')}:</span> {card.how}
        </p>
      )}
    </Card>
  );
}
